import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, mediaSrc } from "../../lib/api";

const LOGO_H = { platinum: "h-24", gold: "h-20", silver: "h-16", bronze: "h-12" };

export function useSponsors(list) {
  const [s, setS] = useState(list || []);
  useEffect(() => { if (!list) api.get("/public/sponsors").then((r) => setS(r.data)).catch(() => setS([])); }, [list]);
  return list || s;
}

const Linked = ({ s, children, className = "" }) => (s.website
  ? <a href={s.website} target="_blank" rel="noopener noreferrer" className={className} data-testid={`sponsor-link-${s.id}`}>{children}</a>
  : <span className={className}>{children}</span>);

export function SponsorWall({ sponsors, heading = "Our Sponsors" }) {
  if (!sponsors.length) return null;
  const groups = [];
  sponsors.forEach((s) => { const g = groups.find((x) => x.name === s.package_name); g ? g.items.push(s) : groups.push({ name: s.package_name, tier: s.tier, items: [s] }); });
  return (
    <section className="bg-paleblue py-14" data-testid="sponsorship-approved-sponsors">
      <div className="container-site">
        <h2 className="text-center text-3xl text-navy">{heading}</h2>
        {groups.map((g) => (
          <div key={g.name} className="mt-10" data-testid={`sponsor-tier-${g.tier || g.name}`}>
            <h3 className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-burgundy">{g.name} Sponsors</h3>
            <ul className="mt-5 flex flex-wrap items-center justify-center gap-6">
              {g.items.map((s) => (
                <li key={s.id} className="rounded-lg bg-white px-6 py-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`approved-sponsor-${s.id}`}>
                  <Linked s={s} className="flex items-center justify-center">
                    {s.logo_url ? <img src={mediaSrc(s.logo_url)} alt={s.company} className={`${LOGO_H[s.tier] || "h-14"} w-auto max-w-[240px] object-contain`} loading="lazy" />
                      : <span className="font-serif text-lg text-navy">{s.company}</span>}
                  </Linked>
                </li>))}
            </ul>
          </div>))}
      </div>
    </section>
  );
}

export function SponsorBanners({ sponsors: list }) {
  const banners = useSponsors(list).filter((s) => s.banner_url);
  const [i, setI] = useState(0);
  if (!banners.length) return null;
  const s = banners[i % banners.length];
  const go = (d) => setI((x) => (x + d + banners.length) % banners.length);
  return (
    <section className="border-y-2 border-gold bg-white py-8" aria-label="Sponsor banners" data-testid="sponsor-banners">
      <div className="container-site">
        <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.25em] text-navy">With thanks to our sponsors</p>
        <div className="relative">
          <Linked s={s} className="flex h-40 items-center justify-center overflow-hidden rounded-lg bg-slate-100 p-2 sm:h-56 lg:h-64">
            <img src={mediaSrc(s.banner_url)} alt={`${s.company} (${s.package_name} Sponsor)`} className="h-full w-full object-contain" data-testid="sponsor-banner-image" />
          </Linked>
          {banners.length > 1 && <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous sponsor banner" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-navy/80 p-2 text-white hover:bg-navy" data-testid="sponsor-banner-prev"><ChevronLeft size={20} aria-hidden="true" /></button>
            <button type="button" onClick={() => go(1)} aria-label="Next sponsor banner" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-navy/80 p-2 text-white hover:bg-navy" data-testid="sponsor-banner-next"><ChevronRight size={20} aria-hidden="true" /></button>
          </>}
        </div>
        <p className="mt-2 text-center text-sm text-[#4A5568]" aria-live="polite" data-testid="sponsor-banner-caption">{s.company} · {s.package_name} Sponsor{banners.length > 1 ? ` (${(i % banners.length) + 1} of ${banners.length})` : ""}</p>
      </div>
    </section>
  );
}
